import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { ComboCheckbox, FormikTextField } from '@lendica/components';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import { validateWorkEmail } from '../api';
import React from 'react';

interface LeadFormStepOneValues {
    goals: string[];
    email: string;
}

interface LeadFormStepOneProps {
    initialValues: LeadFormStepOneValues;
    onSubmit: (values: LeadFormStepOneValues) => void | Promise<void>;
    emailDisabled?: boolean;
}

const validationSchema = yup.object({
    goals: yup
        .array()
        .of(yup.string())
        .min(1, 'Please select at least one option')
        .required('Please select your goals'),
    email: yup
        .string()
        .email('Please enter a valid email address')
        .required('Work email is required')
        .test('is-work-email', 'Use your company email address to continue.', async value => {
            if (!value) return true;

            try {
                const result = await validateWorkEmail(value);
                // console.log('Validation result:', result);

                // If it's a personal email, validation fails
                if (result.isPersonalEmail) {
                    return false;
                }

                return true;
            } catch (error) {
                // console.error('Validation error:', error);
                return true; // Don't block on API failure
            }
        }),
});

export const LeadFormStepOne = ({
    initialValues,
    onSubmit,
    emailDisabled = false,
}: LeadFormStepOneProps) => {
    const options = [
        {
            value: 'capture_vendor_early_pay_discounts',
            label: 'Capture vendor early‑pay discounts.',
        },
        { value: 'catch_up_on_bills', label: 'Catch up on bills.' },
        { value: 'turn_ar_to_cash', label: 'Turn A/R to cash.' },
        { value: 'offer_net_60_or_more', label: 'Offer net‑60 or more.' },
        { value: 'intelligence_tools_ar', label: 'Intelligence tools to streamline my A/R.' },
        // { value: 'secure_rainy_day_buffer', label: 'Secure rainy-day buffer.' },
    ];

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={false}
            enableReinitialize={false}
        >
            {({
                isSubmitting,
                errors,
                touched,
                isValidating,
                dirty,
                isValid,
                setFieldTouched,
                values,
            }) => {
                // console.log('Email field state:', {
                //     error: errors.email,
                //     touched: touched.email,
                //     isValidating,
                // });

                // Ensure email field is marked as touched when it has an error
                React.useEffect(() => {
                    if (errors.email && !touched.email && values.email) {
                        setFieldTouched('email', true);
                    }
                }, [errors.email, touched.email, values.email, setFieldTouched]);

                const isButtonDisabled = isSubmitting || !dirty || isValidating || !isValid;

                return (
                    <Box
                        component={Form}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            width: 380,
                            maxWidth: '100%',
                        }}
                    >
                        <ComboCheckbox
                            name="goals"
                            label="No wrong answers——you can edit later."
                            options={options}
                            helperText="Select all that apply."
                            required
                        />
                        <FormikTextField
                            name="email"
                            label="Work email"
                            type="email"
                            fullWidth
                            required
                            size="medium"
                            helperText="Enter your business email address"
                            disabled={emailDisabled}
                        />
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            disabled={isButtonDisabled}
                            loading={isSubmitting}
                        >
                            Next
                        </LoadingButton>
                    </Box>
                );
            }}
        </Formik>
    );
};
